import { useEditor } from '@candulabs/craft';
import flatten from 'lodash/flatten';
import { useMemo } from 'react';

// we must do this explicit import to prevent tests from failing...
import { Action, CATEGORIES, CraftNode, HANDLER_TYPES } from 'craft/types';

import { useFlow } from './useFlow';

interface Edge {
  fromStepId: string;
  toStepId: string;
  actionNodeId: string;
  handlerType: HANDLER_TYPES;
}

interface Node {
  stepId: string;
  nodeId: string;
  name: string;
  distance: number;
}

interface Graph {
  edges: Edge[];
  nodes: Node[];
  isStepConnected: (step: string) => boolean;
}

export const useFlowGraph = (nodeId: string): Graph => {
  const { query } = useEditor();
  const flow = useFlow(nodeId);

  // first, we capture all the nodes rapresented as stepIds
  const stepNodeIds = query.node(nodeId).get().data.nodes;
  const graphNodes = flow.stepIds.map((stepId, index) => ({
    stepId,
    nodeId: stepNodeIds[index],
    name: `Step ${index + 1}`,
    // the distance from the initial node. For now, -1 as we have not
    // calculated any distance
    distance: -1,
  }));

  const getEdgesForNode = (node: CraftNode, stepId: string) => {
    const { actions } = node.data.custom || {};
    const { submitGoToFlowStepId } = node.data.props || {};

    if (submitGoToFlowStepId) {
      return [
        {
          actionNodeId: node.id,
          handlerType: HANDLER_TYPES.FLOW_SET_STEP,
          fromStepId: stepId,
          toStepId: submitGoToFlowStepId,
        },
      ];
    }

    if (!actions || !actions.length || !actions[0]) {
      return [];
    }

    return actions.reduce((acc: Edge[], action: Action) => {
      if (action.category !== CATEGORIES.FLOW) {
        return acc;
      }
      const type = action.handler?.type;
      if (!type) {
        return acc;
      }

      const actionNodeId = node.id;

      if (type === HANDLER_TYPES.FLOW_NEXT_STEP) {
        return [
          ...acc,
          {
            actionNodeId,
            handlerType: type,
            fromStepId: stepId,
            toStepId: flow.getNextStepId(stepId),
          },
        ];
      }
      if (type === HANDLER_TYPES.FLOW_PREVIOUS_STEP) {
        return [
          ...acc,
          {
            actionNodeId,
            handlerType: type,
            fromStepId: stepId,
            toStepId: flow.getPreviousStepId(stepId),
          },
        ];
      }
      if (type === HANDLER_TYPES.FLOW_SET_STEP) {
        return [
          ...acc,
          {
            actionNodeId,
            handlerType: type,
            fromStepId: stepId,
            // @ts-ignore
            toStepId: action.handler.stepId,
          },
        ];
      }

      return acc;
    }, []);
  };

  const findEdges = (graphNodeId: string, stepId: string): Edge[] => {
    const node = query.node(graphNodeId).get();
    if (!node) {
      return [];
    }
    const edges = getEdgesForNode(node as CraftNode, stepId);

    return flatten([edges, ...(node.data.nodes || []).map((n) => findEdges(n, stepId))]);
  };

  const edges = flatten(
    graphNodes.map(({ nodeId: graphNodeId, stepId }) => findEdges(graphNodeId, stepId)),
  );

  const findDistance = (
    currentStepId: string,
    currentDistance: number,
    visitedSteps: Set<string>,
  ): any => {
    // I've already seen this node. Don't come back...
    if (visitedSteps.has(currentStepId)) {
      return {};
    }
    const updatedVisitedSteps = visitedSteps.add(currentStepId);
    // let's check out all the beautiful nodes that this node is going to
    const moreDistances = edges
      .filter((edge) => edge.fromStepId === currentStepId)
      .reduce(
        (obj, edge) => ({
          ...obj,
          ...findDistance(edge.toStepId, currentDistance + 1, updatedVisitedSteps),
        }),
        {},
      );

    return { ...moreDistances, [currentStepId]: currentDistance };
  };

  const distances = useMemo(() => findDistance(graphNodes[0].stepId, 0, new Set()), [graphNodes]);
  const nodesWithDistances = graphNodes.map((node) => ({
    ...node,
    distance: distances[node.stepId] || node.distance,
  }));

  const isStepConnected = (stepId: string) => distances[stepId] > -1;

  return {
    nodes: nodesWithDistances,
    edges,
    isStepConnected,
  };
};
